<template>
  <div class="px-2 pt-2 pb-4">
    <div class="d-flex align-center mb-4">
      <v-icon class="mr-3" color="primary">mdi-account-group</v-icon>
      <span class="text-h5 font-weight-bold">Provider Review</span>
    </div>
    <div class="pa-6 pt-0">
      <v-row v-if="showFullPageLoading" class="text-center py-8">
        <v-col cols="12">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          <div class="text-body-2 text-medium-emphasis mt-2">
            <template v-if="activeServiceProviderName">
              Loading results for
              <strong>{{ activeServiceProviderName }}</strong>
            </template>
            <template v-else>Loading…</template>
          </div>
        </v-col>
      </v-row>
      <template v-else>
        <v-row v-if="showProviderComparisonUpgradeBanner" class="mb-4">
            <v-col cols="12">
              <div class="upgrade-gate-banner brand-gate-card">
                <div class="d-flex align-center justify-space-between flex-wrap ga-3">
                  <div>
                    <div class="font-weight-bold mb-1 brand-gate-card__title">See how your providers compare</div>
                    <div class="text-body-2 brand-gate-card__body">
                      Upgrade to compare your brand against unlimited providers on paid plans.
                    </div>
                  </div>
                  <v-btn
                    variant="flat"
                    class="brand-upgrade-cta-btn text-none font-weight-bold"
                    @click="$router.push('/profile?tab=billing')"
                  >
                    Upgrade Your Plan
                  </v-btn>
                </div>
              </div>
            </v-col>
        </v-row>
        <v-row v-if="providerComparisonError" class="mb-4">
          <v-col cols="12">
            <v-alert
              type="error"
              variant="tonal"
              closable
              @click:close="providerComparisonError = ''"
            >
              {{ providerComparisonError }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row class="mb-4 provider-picker-row align-end">
          <v-col cols="12" md="7" lg="8">
            <v-menu
              v-model="providerMenuOpen"
              :close-on-content-click="false"
              location="bottom start"
              max-width="520"
              min-width="320"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-bind="props"
                  :model-value="selectedComparisonProvider || ''"
                  label="Provider comparisons"
                  placeholder="Provider comparisons"
                  prepend-inner-icon="mdi-magnify"
                  append-inner-icon="mdi-menu-down"
                  variant="outlined"
                  readonly
                  hide-details
                ></v-text-field>
              </template>
              <v-card>
                <v-list density="comfortable">
                  <v-list-item
                    v-for="providerName in filteredMergedProviderNames"
                    :key="providerName"
                    :active="providerName === selectedComparisonProvider"
                    @click="selectProvider(providerName)"
                  >
                    <v-list-item-title>{{ providerName }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!filteredMergedProviderNames.length"
                    disabled
                  >
                    <v-list-item-title class="text-medium-emphasis">No providers found</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </v-col>
          <v-col v-if="canCreateProvider && !freePlanProviderCapReached" cols="12" md="5" lg="4" class="d-flex justify-md-end">
            <v-btn
              color="primary"
              variant="elevated"
              size="large"
              rounded="lg"
              class="add-provider-action-btn"
              prepend-icon="mdi-account-plus-outline"
              @click="openAddProviderDialog"
            >
              Add provider
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="showProviderComparisonProgressBanner" class="mb-4">
          <v-col cols="12">
            <v-alert type="info" variant="tonal">
              <div class="d-flex align-center">
                <v-progress-circular indeterminate size="18" width="2" color="info" class="mr-2"></v-progress-circular>
                <span>Provider comparison in progress. We will update the table when it is ready.</span>
              </div>
            </v-alert>
          </v-col>
        </v-row>
        <v-row v-if="providerPollTimedOut" class="mb-4">
          <v-col cols="12">
            <v-alert type="warning" variant="tonal" closable @click:close="providerPollTimedOut = false">
              Still waiting on all models for this provider. Try refreshing the page in a moment, or switch away and back to this tab.
            </v-alert>
          </v-col>
        </v-row>

        <v-row v-if="showNoComparisonState" class="text-center py-8">
          <v-col cols="12">
            <v-alert type="info" variant="tonal">
              No provider comparison data yet. Add a provider above to start comparison jobs.
            </v-alert>
          </v-col>
        </v-row>

        <!-- Provider comparison table (deferred while service_provider jobs are in flight; see all_provider_llm_jobs_complete) -->
        <v-row v-else-if="showProviderComparisonTable">
          <v-col cols="12">
            <v-card variant="outlined" class="provider-comparison-card">
              <v-card-title class="px-4 py-3 d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                  <v-icon class="mr-3" color="primary">mdi-table-large</v-icon>
                  Provider Comparison
                  <v-tooltip
                    v-if="showCitationsTooltip"
                    location="top"
                    max-width="280"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="grey" class="ml-2">mdi-information-outline</v-icon>
                    </template>
                    <span>Gemini grounding didn't return per-claim source mappings for this comparison; the model relied on training data.</span>
                  </v-tooltip>
                </div>
              </v-card-title>
              <v-card-text class="pt-0 pb-2 px-2">
                <div class="attribute-sentiment-table-container">
                  <table class="attribute-sentiment-table">
                    <thead>
                      <tr>
                        <th class="brand-column">Provider</th>
                        <th class="metric-column">Practice Type</th>
                        <th class="metric-column">
                          <div class="signal-header">
                            <span class="signal-header-line">{{ specializationSignalLabel }}</span>
                          </div>
                        </th>
                        <th class="metric-column">Training &amp; Credentials (Public)</th>
                        <th class="metric-column">Volume / Outcome Signals</th>
                        <th class="metric-column">Service Classification</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in providersTableRows"
                        :key="row.name"
                        :class="{ 'primary-product-row': row.is_primary }"
                      >
                        <td :class="['brand-cell', { 'primary-brand-cell': row.is_primary }]">
                          <div class="d-flex align-center">
                            <span class="font-weight-medium mr-2">
                              {{ row.name }}
                            </span>
                            <v-icon
                              v-if="row.is_primary"
                              size="x-small"
                              color="amber"
                              class="ml-1"
                            >
                              mdi-star
                            </v-icon>
                          </div>
                        </td>
                        <td class="reasoning-cell ranking-cell-citations">
                          <div class="reasoning-items-container">
                            <template v-for="(reason, index) in visibleRankingReasons(row.practice_type_reasons)" :key="`${row.name}-practice_type-${index}`">
                              <div class="reasoning-card-wrapper">
                                <div
                                  :class="[{ 'reasoning-card-grounded': reasonHasCitations(reason) }]"
                                  class="plain-reasoning-row"
                                  @click="reasonHasCitations(reason) && toggleCitation(`${row.name}-practice_type-${index}`)"
                                >
                                  <span class="reasoning-text">{{ primaryReasoningText(row, reason, 'practice_type') }}</span>
                                  <v-icon
                                    v-if="reasonHasCitations(reason)"
                                    size="x-small"
                                    color="success"
                                    class="ml-auto reasoning-grounded-icon"
                                  >
                                    {{ expandedCitations[`${row.name}-practice_type-${index}`] ? 'mdi-chevron-up' : 'mdi-link-variant' }}
                                  </v-icon>
                                </div>
                                <div
                                  v-if="reasonHasCitations(reason) && expandedCitations[`${row.name}-practice_type-${index}`]"
                                  :class="getCitationDetailsClass(row.productModel, 'practice_type')"
                                  class="citation-details"
                                >
                                  <div class="citation-header">
                                    <v-icon size="x-small" class="mr-1">mdi-check-decagram</v-icon>
                                    <span>LLM verified from:</span> 
                                  </div>
                                  <div v-if="reason.citation_urls[0]?.snippet" class="citation-snippet">
                                    <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                    {{ truncateText(reason.citation_urls[0].snippet, 120) }}
                                  </div>
                                  <div class="citation-links">
                                    <a
                                      v-for="(link, linkIdx) in reason.citation_urls"
                                      :key="linkIdx"
                                      :href="citationLinkHref(link)"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="citation-source-link"
                                      @click.stop
                                    >
                                      <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                                      {{ truncateText(citationLinkTitle(link), 50) }}
                                      <v-tooltip v-if="link.validated" location="top">
                                        <template v-slot:activator="{ props }">
                                          <v-icon v-bind="props" size="x-small" color="success" class="ml-1">mdi-check-decagram</v-icon>
                                        </template>
                                        <span>Verified URL</span>
                                      </v-tooltip>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <span v-if="!visibleRankingReasons(row.practice_type_reasons).length" class="text-grey">—</span>
                          </div>
                        </td>
                        <td class="reasoning-cell ranking-cell-citations">
                          <div class="reasoning-items-container">
                            <template v-for="(reason, index) in visibleRankingReasons(row.specialization_signal_reasons)" :key="`${row.name}-specialization_signal-${index}`">
                              <div class="reasoning-card-wrapper">
                                <div
                                  :class="[getReasoningCardClass(row.productModel, 'specialization_signal'), { 'reasoning-card-grounded': reasonHasCitations(reason) }]"
                                  class="reasoning-card"
                                  @click="reasonHasCitations(reason) && toggleCitation(`${row.name}-specialization_signal-${index}`)"
                                >
                                  <span class="reasoning-text">{{ primaryReasoningText(row, reason, 'specialization_signal') }}</span>
                                  <v-icon
                                    v-if="reasonHasCitations(reason)"
                                    size="x-small"
                                    color="success"
                                    class="ml-auto reasoning-grounded-icon"
                                  >
                                    {{ expandedCitations[`${row.name}-specialization_signal-${index}`] ? 'mdi-chevron-up' : 'mdi-link-variant' }}
                                  </v-icon>
                                  <span v-if="getMetricDisplayValue(row.productModel, 'specialization_signal') != null" class="reasoning-card-metric-value">
                                    {{ getMetricDisplayValue(row.productModel, 'specialization_signal') }}
                                  </span>
                                </div>
                                <div
                                  v-if="reasonHasCitations(reason) && expandedCitations[`${row.name}-specialization_signal-${index}`]"
                                  :class="getCitationDetailsClass(row.productModel, 'specialization_signal')"
                                  class="citation-details"
                                >
                                  <div class="citation-header">
                                    <v-icon size="x-small" class="mr-1">mdi-check-decagram</v-icon>
                                    <span>LLM verified from:</span>
                                  </div>
                                  <div v-if="reason.citation_urls[0]?.snippet" class="citation-snippet">
                                    <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                    {{ truncateText(reason.citation_urls[0].snippet, 120) }}
                                  </div>
                                  <div
                                    v-else-if="reason.text && String(reason.text).trim()"
                                    class="citation-snippet"
                                  >
                                    <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                    {{ truncateText(reason.text, 120) }}
                                  </div>
                                  <div class="citation-links">
                                    <a
                                      v-for="(link, linkIdx) in reason.citation_urls"
                                      :key="linkIdx"
                                      :href="citationLinkHref(link)"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="citation-source-link"
                                      @click.stop
                                    >
                                      <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                                      {{ truncateText(citationLinkTitle(link), 50) }}
                                      <v-tooltip v-if="link.validated" location="top">
                                        <template v-slot:activator="{ props }">
                                          <v-icon v-bind="props" size="x-small" color="success" class="ml-1">mdi-check-decagram</v-icon>
                                        </template>
                                        <span>Verified URL</span>
                                      </v-tooltip>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <template v-if="!visibleRankingReasons(row.specialization_signal_reasons).length">
                              <div
                                v-if="row.specialization_band"
                                :class="[getReasoningCardClass(row.productModel, 'specialization_signal'), 'reasoning-card']"
                              >
                                <span class="reasoning-text">{{ row.specialization_band }}</span>
                                <span v-if="getMetricDisplayValue(row.productModel, 'specialization_signal') != null" class="reasoning-card-metric-value">
                                  {{ getMetricDisplayValue(row.productModel, 'specialization_signal') }}
                                </span>
                              </div>
                              <span v-else class="text-grey">—</span>
                            </template>
                          </div>
                        </td>
                        <td class="reasoning-cell ranking-cell-citations">
                          <div class="reasoning-items-container">
                            <template v-for="(reason, index) in visibleRankingReasons(row.training_credentials_reasons)" :key="`${row.name}-training_credentials-${index}`">
                              <div class="reasoning-card-wrapper">
                                <div
                                  :class="[{ 'reasoning-card-grounded': reasonHasCitations(reason) }]"
                                  class="plain-reasoning-row"
                                  @click="reasonHasCitations(reason) && toggleCitation(`${row.name}-training_credentials-${index}`)"
                                >
                                  <span class="reasoning-text">{{ primaryReasoningText(row, reason, 'training_credentials') }}</span>
                                  <v-icon
                                    v-if="reasonHasCitations(reason)"
                                    size="x-small"
                                    color="success"
                                    class="ml-auto reasoning-grounded-icon"
                                  >
                                    {{ expandedCitations[`${row.name}-training_credentials-${index}`] ? 'mdi-chevron-up' : 'mdi-link-variant' }}
                                  </v-icon>
                                </div>
                                <div
                                  v-if="reasonHasCitations(reason) && expandedCitations[`${row.name}-training_credentials-${index}`]"
                                  :class="getCitationDetailsClass(row.productModel, 'training_credentials')"
                                  class="citation-details"
                                >
                                  <div class="citation-header">
                                    <v-icon size="x-small" class="mr-1">mdi-check-decagram</v-icon>
                                    <span>LLM verified from:</span>
                                  </div>
                                  <div v-if="reason.citation_urls[0]?.snippet" class="citation-snippet">
                                    <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                    {{ truncateText(reason.citation_urls[0].snippet, 120) }}
                                  </div>
                                  <div class="citation-links">
                                    <a
                                      v-for="(link, linkIdx) in reason.citation_urls"
                                      :key="linkIdx"
                                      :href="citationLinkHref(link)"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="citation-source-link"
                                      @click.stop
                                    >
                                      <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                                      {{ truncateText(citationLinkTitle(link), 50) }}
                                      <v-tooltip v-if="link.validated" location="top">
                                        <template v-slot:activator="{ props }">
                                          <v-icon v-bind="props" size="x-small" color="success" class="ml-1">mdi-check-decagram</v-icon>
                                        </template>
                                        <span>Verified URL</span>
                                      </v-tooltip>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <span v-if="!visibleRankingReasons(row.training_credentials_reasons).length" class="text-grey">—</span>
                          </div>
                        </td>
                        <td class="reasoning-cell ranking-cell-citations">
                          <div class="reasoning-items-container">
                            <template v-for="(reason, index) in visibleRankingReasons(row.volume_outcome_reasons)" :key="`${row.name}-volume_outcome_signals-${index}`">
                              <div class="reasoning-card-wrapper">
                                <div
                                  :class="[{ 'reasoning-card-grounded': reasonHasCitations(reason) }]"
                                  class="plain-reasoning-row"
                                  @click="reasonHasCitations(reason) && toggleCitation(`${row.name}-volume_outcome_signals-${index}`)"
                                >
                                  <span class="reasoning-text">{{ primaryReasoningText(row, reason, 'volume_outcome_signals') }}</span>
                                  <v-icon
                                    v-if="reasonHasCitations(reason)"
                                    size="x-small"
                                    color="success"
                                    class="ml-auto reasoning-grounded-icon"
                                  >
                                    {{ expandedCitations[`${row.name}-volume_outcome_signals-${index}`] ? 'mdi-chevron-up' : 'mdi-link-variant' }}
                                  </v-icon>
                                </div>
                                <div
                                  v-if="reasonHasCitations(reason) && expandedCitations[`${row.name}-volume_outcome_signals-${index}`]"
                                  :class="getCitationDetailsClass(row.productModel, 'volume_outcome_signals')"
                                  class="citation-details"
                                >
                                  <div class="citation-header">
                                    <v-icon size="x-small" class="mr-1">mdi-check-decagram</v-icon>
                                    <span>LLM verified from:</span>
                                  </div>
                                  <div v-if="reason.citation_urls[0]?.snippet" class="citation-snippet">
                                    <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                    {{ truncateText(reason.citation_urls[0].snippet, 120) }}
                                  </div>
                                  <div class="citation-links">
                                    <a
                                      v-for="(link, linkIdx) in reason.citation_urls"
                                      :key="linkIdx"
                                      :href="citationLinkHref(link)"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="citation-source-link"
                                      @click.stop
                                    >
                                      <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                                      {{ truncateText(citationLinkTitle(link), 50) }}
                                      <v-tooltip v-if="link.validated" location="top">
                                        <template v-slot:activator="{ props }">
                                          <v-icon v-bind="props" size="x-small" color="success" class="ml-1">mdi-check-decagram</v-icon>
                                        </template>
                                        <span>Verified URL</span>
                                      </v-tooltip>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <span v-if="!visibleRankingReasons(row.volume_outcome_reasons).length" class="text-grey">—</span>
                          </div>
                        </td>
                        <td class="reasoning-cell ranking-cell-citations">
                          <div class="reasoning-items-container">
                            <template v-for="(reason, index) in visibleRankingReasons(row.classification_reasons)" :key="`${row.name}-llm_classification-${index}`">
                              <div class="reasoning-card-wrapper">
                                <div
                                  :class="[{ 'reasoning-card-grounded': reasonHasCitations(reason) }]"
                                  class="plain-reasoning-row"
                                  @click="reasonHasCitations(reason) && toggleCitation(`${row.name}-llm_classification_of_service-${index}`)"
                                >
                                  <span class="reasoning-text">{{ primaryReasoningText(row, reason, 'llm_classification_of_service') }}</span>
                                  <v-icon
                                    v-if="reasonHasCitations(reason)"
                                    size="x-small"
                                    color="success"
                                    class="ml-auto reasoning-grounded-icon"
                                  >
                                    {{ expandedCitations[`${row.name}-llm_classification_of_service-${index}`] ? 'mdi-chevron-up' : 'mdi-link-variant' }}
                                  </v-icon>
                                </div>
                                <div
                                  v-if="reasonHasCitations(reason) && expandedCitations[`${row.name}-llm_classification_of_service-${index}`]"
                                  :class="getCitationDetailsClass(row.productModel, 'llm_classification_of_service')"
                                  class="citation-details"
                                >
                                  <div class="citation-header">
                                    <v-icon size="x-small" class="mr-1">mdi-check-decagram</v-icon>
                                    <span>LLM verified from:</span>
                                  </div>
                                  <div v-if="reason.citation_urls[0]?.snippet" class="citation-snippet">
                                    <v-icon size="x-small" class="mr-1">mdi-format-quote-open</v-icon>
                                    {{ truncateText(reason.citation_urls[0].snippet, 120) }}
                                  </div>
                                  <div class="citation-links">
                                    <a
                                      v-for="(link, linkIdx) in reason.citation_urls"
                                      :key="linkIdx"
                                      :href="citationLinkHref(link)"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="citation-source-link"
                                      @click.stop
                                    >
                                      <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                                      {{ truncateText(citationLinkTitle(link), 50) }}
                                      <v-tooltip v-if="link.validated" location="top">
                                        <template v-slot:activator="{ props }">
                                          <v-icon v-bind="props" size="x-small" color="success" class="ml-1">mdi-check-decagram</v-icon>
                                        </template>
                                        <span>Verified URL</span>
                                      </v-tooltip>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <span v-if="!visibleRankingReasons(row.classification_reasons).length" class="text-grey">—</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </div>

    <v-dialog v-model="showAddProviderDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Add New Provider</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newProviderName"
            label="Provider name"
            placeholder="e.g., Dr. Jane Smith"
            variant="outlined"
            hide-details
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelAddProvider">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="addingProvider"
            :disabled="!newProviderName.trim()"
            @click="addProviderComparison"
          >
            Add Provider
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from '@/axios.js'

const BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'ProviderReviewTab',
  emits: ['provider-names-changed', 'provider-comparison-pending'],
  props: {
    assessmentId: {
      type: Number,
      default: null
    },
    selectedProvider: {
      type: String,
      default: null
    },
    canCreateProvider: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      comparisonData: null,
      loadingComparison: false,
      expandedCitations: {},
      // Provider list is sourced from comparisons.target_name on the backend.
      providerNames: [],
      selectedComparisonProvider: '',
      providerSearch: '',
      providerMenuOpen: false,
      showAddProviderDialog: false,
      newProviderName: '',
      addingProvider: false,
      isWaitingForProviderComparison: false,
      pendingProviderName: '',
      refreshTimer: null,
      allProviderLlmJobsComplete: false,
      /** True when the selected provider comparison is complete across providers. */
      pendingAddAllLlmComplete: true,
      providerPollTimedOut: false,
      providerComparisonError: ''
    }
  },
  computed: {
    ...mapGetters(['teamPlanTierKey']),
    /** Distinct provider names (case-insensitive), including a pending add. */
    distinctProviderNameCount() {
      const keys = new Set(
        this.mergedProviderNames.map((n) => String(n).trim().toLowerCase()).filter(Boolean)
      )
      return keys.size
    },
    /** Free tier (no paid team plan): only one distinct provider comparison allowed. */
    freePlanProviderCapReached() {
      return this.teamPlanTierKey === 'free' && this.distinctProviderNameCount >= 1
    },
    showProviderComparisonUpgradeBanner() {
      return this.canCreateProvider && this.freePlanProviderCapReached
    },
    /** Service provider (human) name — not the LLM backend. */
    activeServiceProviderName() {
      return (this.pendingProviderName || this.selectedComparisonProvider || '').trim()
    },
    /** Dropdown list: API names plus the provider being added (if not present yet). */
    mergedProviderNames() {
      const set = new Set(
        (this.providerNames || []).map((n) => (n != null ? String(n).trim() : '')).filter(Boolean)
      )
      if (this.pendingProviderName) set.add(this.pendingProviderName.trim())
      return [...set].sort()
    },
    showProviderComparisonTable() {
      if (this.providersTableRows.length === 0) return false
      if (
        this.isWaitingForProviderComparison &&
        this.pendingProviderName &&
        !this.pendingAddAllLlmComplete &&
        this.selectedComparisonProvider === this.pendingProviderName
      ) {
        return false
      }
      return true
    },
    /** In-progress banner only while waiting and the table is not yet shown (avoids stacking alert on loaded table). */
    showProviderComparisonProgressBanner() {
      return this.isWaitingForProviderComparison && !this.showProviderComparisonTable
    },
    filteredMergedProviderNames() {
      const search = (this.providerSearch || '').trim().toLowerCase()
      if (!search) return this.mergedProviderNames
      return this.mergedProviderNames.filter((name) => name.toLowerCase().includes(search))
    },
    specializationSignalLabel() {
      return (
        this.comparisonData?.metrics_description?.specialization_signal ||
        'Specialization Signal'
      )
    },
    providersTableRows() {
      if (!this.comparisonData?.products || !this.comparisonData.products.length) return []

      const products = [...this.comparisonData.products]

      const toNumberOrNull = (value) => {
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string' && value.trim() !== '') {
          const parsed = Number.parseFloat(value)
          return Number.isFinite(parsed) ? parsed : null
        }
        return null
      }

      const extractReasonItemText = (reasonItem) => {
        if (reasonItem == null) return ''
        if (typeof reasonItem === 'string' || typeof reasonItem === 'number' || typeof reasonItem === 'boolean') {
          return String(reasonItem)
        }
        if (typeof reasonItem === 'object') {
          return (
            reasonItem.text ??
            reasonItem.content ??
            reasonItem.value ??
            reasonItem.snippet ??
            reasonItem.reason ??
            ''
          )
        }
        return ''
      }

      const getReasonText = (reasoning, key) => {
        if (!reasoning || typeof reasoning !== 'object') return ''
        const raw = reasoning[key]
        if (raw == null) return ''
        // New prompt format: each reasoning field is a single string.
        // Backward compatibility: if older data stored an array, join it into one string.
        if (Array.isArray(raw)) return raw.map(extractReasonItemText).filter(Boolean).join(' ')
        return extractReasonItemText(raw)
      }

      // Sort by specialization signal (descending) for a stable row order
      products.sort((a, b) => {
        const aScore = toNumberOrNull(a?.metrics?.specialization_signal) ?? 0
        const bScore = toNumberOrNull(b?.metrics?.specialization_signal) ?? 0
        return bScore - aScore
      })

      return products.map((p) => {
        // Some grounded responses can return slightly different JSON shapes;
        // normalize at the UI layer so the table stays readable.
        let reasoning = p?.reasoning ?? {}
        if (typeof reasoning === 'string') {
          try {
            reasoning = JSON.parse(reasoning)
          } catch (e) {
            reasoning = {}
          }
        }
        if (!reasoning || typeof reasoning !== 'object' || Array.isArray(reasoning)) reasoning = {}

        const score = toNumberOrNull(p?.metrics?.specialization_signal)
        const { band } = this.getSpecializationBand(score)

        const productModel = {
          name: p.name,
          is_primary: !!p.is_primary,
          metrics: p.metrics || {},
          reasoning
        }

        return {
          name: p.name,
          is_primary: !!p.is_primary,
          specialization_score: score,
          specialization_band: band,
          practice_type: getReasonText(reasoning, 'practice_type'),
          training_credentials: getReasonText(reasoning, 'training_credentials'),
          volume_outcome_signals: getReasonText(reasoning, 'volume_outcome_signals'),
          classification: getReasonText(reasoning, 'llm_classification_of_service'),
          productModel,
          practice_type_reasons: this.getProductReasoning(productModel, 'practice_type'),
          training_credentials_reasons: this.getProductReasoning(productModel, 'training_credentials'),
          volume_outcome_reasons: this.getProductReasoning(productModel, 'volume_outcome_signals'),
          classification_reasons: this.getProductReasoning(productModel, 'llm_classification_of_service'),
          specialization_signal_reasons: this.getProductReasoning(productModel, 'specialization_signal')
        }
      })
    },
    isGroundedGeminiProvider() {
      const p = (this.selectedProvider || '').toLowerCase()
      return p === 'google_grounded' || p === 'gemini_grounded'
    },
    showCitationsTooltip() {
      if (!this.isGroundedGeminiProvider) return false
      if (!this.comparisonData?.products?.length) return false
      const hasAnyCitations = this.comparisonData.products.some((product) => {
        let reasoning = product.reasoning || {}
        if (typeof reasoning === 'string') {
          try {
            reasoning = JSON.parse(reasoning)
          } catch (e) {
            reasoning = {}
          }
        }
        if (!reasoning || typeof reasoning !== 'object') return false
        return Object.values(reasoning).some((val) => {
          if (Array.isArray(val)) {
            return val.some(
              (item) =>
                typeof item === 'object' &&
                item &&
                Array.isArray(item.citation_urls) &&
                item.citation_urls.length > 0
            )
          }
          if (typeof val === 'object' && val && Array.isArray(val.citation_urls) && val.citation_urls.length > 0) {
            return true
          }
          return false
        })
      })
      return !hasAnyCitations
    },
    primaryProviderMetricKey() {
      if (!this.comparisonData?.products?.length) return 'specialization_signal'
      const m = this.comparisonData.products[0]?.metrics || {}
      const keys = Object.keys(m)
      if (keys.includes('specialization_signal')) return 'specialization_signal'
      const numericKey = keys.find((k) => typeof m[k] === 'number')
      return numericKey || keys[0] || 'specialization_signal'
    },
    providerComparisonScoreScale() {
      if (!this.comparisonData?.products?.length) return 10
      let maxScore = 0
      this.comparisonData.products.forEach((p) => {
        const m = p.metrics || {}
        Object.values(m).forEach((v) => {
          if (typeof v === 'number' && v > maxScore) maxScore = v
        })
      })
      return maxScore <= 5 ? 5 : 10
    },
    showFullPageLoading() {
      if (this.showProviderComparisonTable) return false
      return this.loadingComparison && !this.isWaitingForProviderComparison && !this.comparisonData
    },
    showNoComparisonState() {
      return (
        !this.isWaitingForProviderComparison &&
        (!this.comparisonData || !this.comparisonData.products || this.comparisonData.products.length === 0)
      )
    }
  },
  watch: {
    // Lets the Action Center checklist tick "Add your providers" without a refetch.
    // Includes a provider whose comparison jobs are still running.
    'mergedProviderNames.length'(count) {
      this.$emit('provider-names-changed', count)
    },
    // Diagnosis reads a provider only once its comparison is saved, so Action Center
    // holds Run Diagnosis while one is still being added.
    isWaitingForProviderComparison(waiting) {
      this.$emit('provider-comparison-pending', waiting)
    },
    assessmentId: {
      handler() {
        this.stopRefreshTimer()
        this.pendingProviderName = ''
        this.isWaitingForProviderComparison = false
        this.pendingAddAllLlmComplete = true
        this.fetchProviderComparison({ resetSelectedProvider: true })
      },
      immediate: true
    },
    selectedProvider() {
      if (this.assessmentId) this.fetchProviderComparison()
    },
    selectedComparisonProvider() {
      if (this.assessmentId) this.fetchProviderComparison()
    }
  },
  methods: {
    /**
     * @param {Object} [opts]
     * @param {boolean} [opts.forPendingAddPoll] When true, GETs only the pending new provider, updates job flags + names, and
     *   does not overwrite the table (comparisonData) until all LLM jobs for that name have finished with payload.
     * @param {string|null|undefined} [opts.primaryNameOverride] Force this primary_provider_name (recovery / explicit fetch).
     */
    async fetchProviderComparison({
      resetSelectedProvider = false,
      silent = false,
      forPendingAddPoll = false,
      primaryNameOverride
    } = {}) {
      if (!this.assessmentId) {
        this.comparisonData = null
        this.providerNames = []
        this.selectedComparisonProvider = ''
        this.pendingAddAllLlmComplete = true
        return
      }
      if (forPendingAddPoll) {
        if (!this.pendingProviderName) {
          this.stopRefreshTimer()
          this.isWaitingForProviderComparison = false
          this.pendingAddAllLlmComplete = true
          return
        }
      }
      if (!silent) this.loadingComparison = true
      try {
        const params = this.selectedProvider ? { llm_provider: this.selectedProvider } : {}
        let primaryForRequest = null
        if (forPendingAddPoll) {
          primaryForRequest = this.pendingProviderName
        } else if (primaryNameOverride != null && String(primaryNameOverride).trim() !== '') {
          primaryForRequest = String(primaryNameOverride).trim()
        } else if (this.selectedComparisonProvider) {
          primaryForRequest = this.selectedComparisonProvider
        }
        if (primaryForRequest) params.primary_provider_name = primaryForRequest

        const res = await axios.get(
          `${BASE_URL}/assessments/${this.assessmentId}/provider_comparison`,
          { headers: { Authorization: localStorage.getItem('auth_token') }, params }
        )
        if (!res.data.success) {
          if (!forPendingAddPoll) {
            this.providerNames = []
            this.comparisonData = null
            this.allProviderLlmJobsComplete = false
          }
          return
        }

        if (forPendingAddPoll) {
          const fromApi = Array.isArray(res.data.provider_names) ? res.data.provider_names : []
          this.providerNames = this.uniqSortedNames([...fromApi, this.pendingProviderName].filter(Boolean))
          const selectedProviderComplete =
            res.data.selected_provider_comparison_complete ?? res.data.all_provider_llm_jobs_complete
          this.pendingAddAllLlmComplete = !!selectedProviderComplete
          const bData = res.data.provider_comparison || null
          const bReady = !!(bData && bData.products && bData.products.length)
          if (this.pendingAddAllLlmComplete && bReady) {
            this.comparisonData = bData
            this.selectedComparisonProvider = this.pendingProviderName
            this.allProviderLlmJobsComplete = true
            this.pendingProviderName = ''
            this.isWaitingForProviderComparison = false
            this.providerPollTimedOut = false
            this.stopRefreshTimer()
            this.pendingAddAllLlmComplete = true
            await this.$nextTick()
            await this.fetchProviderComparison({ silent: false })
            return
          }
          if (this.pendingAddAllLlmComplete && !bReady) {
            await this.fetchProviderComparison({
              silent: true,
              primaryNameOverride: this.pendingProviderName
            })
            if (this.comparisonData?.products && this.comparisonData.products.length) {
              this.selectedComparisonProvider = this.pendingProviderName
              this.allProviderLlmJobsComplete = true
              this.pendingProviderName = ''
              this.isWaitingForProviderComparison = false
              this.stopRefreshTimer()
              this.providerPollTimedOut = false
              this.pendingAddAllLlmComplete = true
              await this.$nextTick()
              await this.fetchProviderComparison({ silent: false })
            }
            return
          }
          return
        }

        this.providerNames = Array.isArray(res.data.provider_names) ? res.data.provider_names : []
        if (this.pendingProviderName) {
          this.providerNames = this.uniqSortedNames([...this.providerNames, this.pendingProviderName])
        }
        const selectedProviderComplete =
          res.data.selected_provider_comparison_complete ?? res.data.all_provider_llm_jobs_complete
        this.allProviderLlmJobsComplete = !!selectedProviderComplete
        if (resetSelectedProvider) {
          this.selectedComparisonProvider = res.data.selected_provider_name || this.providerNames[0] || ''
        } else if (!this.selectedComparisonProvider && (res.data.selected_provider_name || this.providerNames.length)) {
          this.selectedComparisonProvider = res.data.selected_provider_name || this.providerNames[0]
        }
        this.comparisonData = res.data.provider_comparison || null
      } catch (err) {
        console.error('Error fetching provider comparison:', err)
        if (!forPendingAddPoll) {
          this.providerNames = this.pendingProviderName
            ? this.uniqSortedNames(this.providerNames.concat(this.pendingProviderName).filter(Boolean))
            : []
          this.comparisonData = null
          this.allProviderLlmJobsComplete = false
        }
      } finally {
        if (!silent) this.loadingComparison = false
      }
    },
    uniqSortedNames(names) {
      const s = new Set(
        (names || []).map((n) => (n == null ? '' : String(n).trim())).filter(Boolean)
      )
      return [...s].sort()
    },
    selectProvider(providerName) {
      this.selectedComparisonProvider = providerName
      this.providerMenuOpen = false
      this.providerSearch = ''
    },
    openAddProviderDialog() {
      if (this.freePlanProviderCapReached) return
      this.providerComparisonError = ''
      this.providerMenuOpen = false
      this.showAddProviderDialog = true
    },
    cancelAddProvider() {
      this.showAddProviderDialog = false
      this.newProviderName = ''
      this.providerComparisonError = ''
    },
    startRefreshTimer() {
      this.stopRefreshTimer()
      this.providerPollTimedOut = false
      const maxAttempts = 50
      let attempts = 0
      const runPoll = async () => {
        if (!this.isWaitingForProviderComparison || !this.pendingProviderName) {
          this.stopRefreshTimer()
          return
        }
        attempts += 1
        await this.fetchProviderComparison({ silent: true, forPendingAddPoll: true })
        if (!this.isWaitingForProviderComparison) {
          this.stopRefreshTimer()
          return
        }
        if (attempts >= maxAttempts) {
          this.isWaitingForProviderComparison = false
          this.providerPollTimedOut = !this.pendingAddAllLlmComplete
          this.pendingProviderName = ''
          this.stopRefreshTimer()
        }
      }
      void runPoll()
      this.refreshTimer = setInterval(() => {
        void runPoll()
      }, 3000)
    },
    stopRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    async addProviderComparison() {
      if (!this.assessmentId) return
      const providerName = this.newProviderName.trim()
      if (!providerName) return

      this.addingProvider = true
      this.providerComparisonError = ''
      try {
        const res = await axios.post(
          `${BASE_URL}/assessments/${this.assessmentId}/provider_comparison`,
          { primary_provider_name: providerName },
          { headers: { Authorization: localStorage.getItem('auth_token') } }
        )
        if (res.data.success) {
          this.cancelAddProvider()
          this.pendingAddAllLlmComplete = false
          this.isWaitingForProviderComparison = true
          this.pendingProviderName = providerName
          this.startRefreshTimer()
        }
      } catch (err) {
        const status = err.response?.status
        const body = err.response?.data
        const message =
          (typeof body?.error === 'string' && body.error) ||
          'Could not start provider comparison.'
        if (status === 422) {
          this.providerComparisonError = message
        } else {
          console.error('Error creating provider comparison:', err)
        }
        this.isWaitingForProviderComparison = false
        this.pendingProviderName = ''
        this.pendingAddAllLlmComplete = true
      } finally {
        this.addingProvider = false
      }
    },
    getSpecializationBand(score) {
      if (score == null || typeof score !== 'number') {
        return { band: null, color: 'grey-lighten-1' }
      }
      if (score >= 9) return { band: 'Very High', color: 'green' }
      if (score >= 7.5) return { band: 'High', color: 'light-green' }
      if (score >= 5.5) return { band: 'Moderate', color: 'amber' }
      if (score >= 3.5) return { band: 'Low–Moderate', color: 'orange' }
      return { band: 'Low', color: 'red' }
    },
    toggleCitation(key) {
      this.expandedCitations = {
        ...this.expandedCitations,
        [key]: !this.expandedCitations[key]
      }
    },
    reasonHasCitations(reason) {
      return !!(reason && reason.citation_urls && reason.citation_urls.length > 0)
    },
    /**
     * Card body line. Signal column is always the band label (Very High / High / …), never the
     * long specialization reasoning string — that appears in the expanded citation panel when present.
     */
    primaryReasoningText(row, reason, columnKey) {
      if (columnKey === 'specialization_signal') {
        return row.specialization_band || '—'
      }
      const t = reason.text && String(reason.text).trim()
      if (t) return reason.text
      if (this.reasonHasCitations(reason)) return 'View sources'
      return ''
    },
    normalizedScoreForScale(score, scale) {
      if (score == null || typeof score !== 'number') return 0
      return scale === 5 ? Math.round(score * 2) : score
    },
    scoringMetricKeyForColumn(product, columnKey) {
      const metrics = product.metrics || {}
      if (typeof metrics[columnKey] === 'number') return columnKey
      return this.primaryProviderMetricKey
    },
    getSentimentBand(product, columnKey) {
      const scoringKey = this.scoringMetricKeyForColumn(product, columnKey)
      const raw = product.metrics?.[scoringKey]
      const score = typeof raw === 'number' && Number.isFinite(raw) ? raw : null
      if (score == null) return 'yellow'
      if (score >= 9) return 'positive'
      if (score >= 6) return 'yellow'
      return 'negative'
    },
    getCitationDetailsClass(product, columnKey) {
      const band = this.getSentimentBand(product, columnKey)
      return `citation-details-${band}`
    },
    getReasoningCardClass(product, columnKey) {
      const band = this.getSentimentBand(product, columnKey)
      return `reasoning-card-${band}`
    },
    getReasoningDotClass(product, columnKey) {
      const band = this.getSentimentBand(product, columnKey)
      return `reasoning-dot-${band}`
    },
    getMetricDisplayValue(product, metricKey) {
      const scale = this.providerComparisonScoreScale
      const raw = product.metrics?.[metricKey]
      if (raw == null || typeof raw !== 'number') return null
      const normalized = this.normalizedScoreForScale(raw, scale)
      return normalized.toFixed(1)
    },
    normalizeCitationUrls(urls) {
      if (!Array.isArray(urls)) return []
      return urls
        .map((link) => {
          if (typeof link === 'string') {
            return { url: link, title: link, validated: false, snippet: null }
          }
          if (!link || typeof link !== 'object') return null
          const url = link.url || link.uri || link.final_url || ''
          const title = link.title || link.uri || url || 'Source'
          return {
            ...link,
            url: String(url),
            title: String(title)
          }
        })
        .filter((u) => u && u.url)
    },
    citationLinkHref(link) {
      if (!link || typeof link !== 'object') return '#'
      return link.url || link.uri || link.final_url || '#'
    },
    citationLinkTitle(link) {
      if (!link || typeof link !== 'object') return ''
      return link.title || link.uri || this.citationLinkHref(link) || ''
    },
    extractReasonItem(reasonItem) {
      if (reasonItem == null) return { text: '', citation_urls: [] }
      if (typeof reasonItem === 'string') return { text: reasonItem, citation_urls: [] }
      if (typeof reasonItem === 'object') {
        const text =
          reasonItem.text ??
          reasonItem.content ??
          reasonItem.value ??
          reasonItem.snippet ??
          reasonItem.reason ??
          ''
        const urls = this.normalizeCitationUrls(reasonItem.citation_urls)
        return { text: String(text), citation_urls: urls }
      }
      return { text: String(reasonItem), citation_urls: [] }
    },
    reasoningItemIsRenderable(r) {
      const t = r.text && String(r.text).trim()
      return !!(t || (r.citation_urls && r.citation_urls.length > 0))
    },
    getProductReasoning(product, columnKey) {
      const reasoning = product.reasoning || {}
      const raw = reasoning[columnKey]
      const hasMetric =
        product.metrics &&
        typeof product.metrics[columnKey] === 'number'

      if (raw != null && raw !== '') {
        if (Array.isArray(raw)) {
          return raw
            .map((item) => this.extractReasonItem(item))
            .filter((r) => this.reasoningItemIsRenderable(r))
        }
        const single = this.extractReasonItem(raw)
        return this.reasoningItemIsRenderable(single) ? [single] : []
      }

      if (hasMetric) {
        return [{ text: '', citation_urls: [] }]
      }
      return []
    },
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) return text || ''
      return text.substring(0, maxLength) + '...'
    },
    visibleRankingReasons(reasons) {
      if (!reasons || !reasons.length) return []
      return reasons.filter(
        (r) =>
          (r.text && String(r.text).trim()) ||
          (r.citation_urls && r.citation_urls.length > 0)
      )
    }
  },
  beforeUnmount() {
    this.stopRefreshTimer()
    // Its add-poll stops here, so it can no longer report the add finishing.
    if (this.isWaitingForProviderComparison) this.$emit('provider-comparison-pending', false)
  }
}
</script>

<style scoped>
.provider-picker-row { row-gap: 12px; }
.add-provider-action-btn {
  width: 100%;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.28) !important;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}
@media (min-width: 960px) {
  .add-provider-action-btn {
    width: auto;
    min-width: 200px;
  }
}
.add-provider-action-btn:hover {
  box-shadow: 0 6px 20px rgba(25, 118, 210, 0.38) !important;
  transform: translateY(-1px);
}
.attribute-sentiment-table-container { overflow-x: auto; width: 100%; }
.attribute-sentiment-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.provider-comparison-card { margin-left: -8px; margin-right: -8px; border-radius: 16px; border-color: var(--brand-sand-300) !important; }
.attribute-sentiment-table thead { background-color: var(--brand-sand-100); position: sticky; top: 0; z-index: 10; }
.attribute-sentiment-table th { padding: 16px 12px; text-align: left; font-weight: 600; color: var(--brand-ink-900); border-bottom: 2px solid var(--brand-sand-300); white-space: nowrap; }
.signal-header { display: flex; flex-direction: column; line-height: 1.25; white-space: normal; }
.signal-header-line { display: block; }
.signal-header-sub { font-weight: 500; opacity: 0.85; }
.attribute-sentiment-table .brand-column { min-width: 200px; position: sticky; left: 0; background-color: var(--brand-sand-100); z-index: 5; box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05); }
.attribute-sentiment-table .metric-column { min-width: 180px; text-align: center; }
.attribute-sentiment-table tbody tr { border-bottom: 1px solid var(--brand-sand-300); }
.attribute-sentiment-table tbody tr:hover { background-color: var(--brand-sand-50); }
.attribute-sentiment-table td { padding: 16px 12px; vertical-align: top; }
.attribute-sentiment-table .brand-cell { position: sticky; left: 0; background-color: #ffffff; z-index: 4; box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05); font-weight: 500; }
.attribute-sentiment-table tbody tr:hover .brand-cell { background-color: #fafafa; }
.primary-product-row { background-color: var(--brand-ocean-100); }
.primary-product-row:hover { background-color: var(--brand-ocean-100) !important; }
.primary-brand-cell { background-color: var(--brand-ocean-100) !important; border-left: 3px solid var(--brand-ocean-600); }
.primary-product-row:hover .primary-brand-cell { background-color: #e4ecf7 !important; }
.reasoning-cell { background-color: transparent; padding: 12px; }
.reasoning-items-container { display: flex; flex-direction: column; gap: 8px; }
.reasoning-card {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  padding-bottom: 18px;
  border-radius: 8px;
  border: 2px solid;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s, transform 0.2s;
  position: relative;
}
.reasoning-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}
.reasoning-card-metric-value { position: absolute; bottom: 4px; right: 10px; font-size: 10px; font-weight: 600; color: rgba(0, 0, 0, 0.45); }
.reasoning-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 10px; flex-shrink: 0; }
.reasoning-text { flex: 1; line-height: 1.4; font-size: 13px; }
.reasoning-card-wrapper { margin-bottom: 8px; }
.plain-reasoning-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}
.reasoning-card-grounded { cursor: pointer; }
.reasoning-card-positive { border-color: #4caf50; background-color: rgba(76, 175, 80, 0.05); }
.reasoning-card-positive:hover { box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3); }
.reasoning-card-neutral { border-color: #1976d2; background-color: rgba(25, 118, 210, 0.05); }
.reasoning-card-neutral:hover { box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3); }
.reasoning-card-yellow { border-color: #facc15; background-color: rgba(250, 204, 21, 0.08); }
.reasoning-card-yellow:hover { box-shadow: 0 4px 12px rgba(250, 204, 21, 0.35); }
.reasoning-card-orange { border-color: #ff9800; background-color: rgba(255, 152, 0, 0.05); }
.reasoning-card-orange:hover { box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3); }
.reasoning-card-negative { border-color: #f44336; background-color: rgba(244, 67, 54, 0.05); }
.reasoning-card-negative:hover { box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3); }
.reasoning-dot-positive { background-color: #4caf50; }
.reasoning-dot-neutral { background-color: #1976d2; }
.reasoning-dot-yellow { background-color: #facc15; }
.reasoning-dot-orange { background-color: #ff9800; }
.reasoning-dot-negative { background-color: #f44336; }
.citation-details { border-top: none; border-radius: 0 0 8px 8px; padding: 10px 12px; margin-top: -4px; animation: slideDown 0.2s ease-out; }
.citation-details-positive { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #86efac; border-top: none; }
.citation-details-neutral { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border: 1px solid #93c5fd; border-top: none; }
.citation-details-yellow { background: linear-gradient(135deg, #fefce8 0%, #fef08a 100%); border: 1px solid #facc15; border-top: none; }
.citation-details-orange { background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%); border: 1px solid #fdba74; border-top: none; }
.citation-details-negative { background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%); border: 1px solid #fca5a5; border-top: none; }
.citation-details-negative .citation-header { color: #991b1b; }
.citation-details-positive .citation-header { color: #166534; }
.citation-details-neutral .citation-header { color: #1d4ed8; }
.citation-details-yellow .citation-header { color: #854d0e; }
.citation-details-orange .citation-header { color: #9a3412; }
.citation-header { display: flex; align-items: center; margin-bottom: 8px; font-size: 12px; font-weight: 500; }
.citation-snippet { display: flex; align-items: flex-start; font-size: 11px; font-style: italic; color: #555; padding: 6px 8px; background: rgba(255, 255, 255, 0.6); border-radius: 6px; border-left: 2px solid #1976d2; line-height: 1.4; margin-bottom: 8px; }
.citation-links { display: flex; flex-wrap: wrap; gap: 6px; }
.citation-source-link { display: inline-flex; align-items: center; padding: 4px 10px; background: rgba(255, 255, 255, 0.8); border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 16px; font-size: 11px; color: #1976d2; text-decoration: none; transition: all 0.2s; }
.citation-source-link:hover { background: white; border-color: #1976d2; box-shadow: 0 2px 4px rgba(25, 118, 210, 0.15); }
.reasoning-grounded-icon { opacity: 0.7; flex-shrink: 0; transition: opacity 0.2s, transform 0.2s; }
.reasoning-card:hover .reasoning-grounded-icon { opacity: 1; }
@keyframes slideDown {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
.citation-snippet .v-icon { flex-shrink: 0; opacity: 0.6; margin-top: 1px; }
.ranking-cell-citations .reasoning-card { align-items: flex-start; }
.ranking-cell-citations .reasoning-text { white-space: pre-wrap; }
</style>
